import { c as _c } from "react/compiler-runtime";
import { NavigationFlyout, NavigationFlyoutData } from '@spa/components/nav/navigation-flyout.component';
import React, { ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import BurgerIconComponent from '@spa/components/burger-icon/burger-icon.component';
import ImageComponent from '@spa/components/image-component/image.component';
import Link from 'next/link';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import PlaceholderComponent from '@spa/components/placeholder/placeholder.component';
import classNames from 'clsx';
import { filterDesktop } from '@spa/components/nav/utils';
import selectTasksPending from '@spa/lib/store/tasks/selectors/selectTasksPending';
import styles from './desktop-navigation.component.module.scss';
import { tracker } from '@spa/lib/tracking/tracker';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import { useSelector } from 'react-redux';
export interface NavigationItemWithFlyoutData {
  readonly id: string | null;
  readonly label: string | null;
  readonly path: string | null;
  readonly flyout: NavigationFlyoutData | null;
  readonly hideDesktop: boolean;
  readonly highlighted: boolean;

  // Needed for mobile-navigation types compatibility
  readonly hideMobile: unknown;
  readonly children: unknown;
}
export interface NavigationMainItem {
  readonly id: string | null;
  readonly label: string | null;
  readonly path: string | null;
  readonly reference: string | null;
  readonly flyout: NavigationFlyoutData | null;
  readonly hideDesktop: boolean;
  readonly highlighted: boolean;
}
export interface NavigationData {
  readonly allCategories: ReadonlyArray<NavigationItemWithFlyoutData>;
  readonly main: ReadonlyArray<NavigationMainItem>;
}

/*
 Reduced view:
 navigation.main.flyout can be null
 navigation.allCategories can be an empty list
 */
export type Props = {
  readonly id: string;
  readonly navigation: NavigationData | null;
  readonly reducedView: boolean;
  readonly showAllCategories: boolean;
};
const PATH_PLACEHOLDER = '/';
const isEmptyFlyout = (flyout: NavigationFlyoutData): boolean => flyout.categories === null && flyout.brands === null && flyout.storeBrands === null && !flyout.promotions?.banner && !flyout.promotions?.product;
const hasOwnPathOrFlyout = (item: {
  path: string | null;
  flyout: NavigationFlyoutData | null;
}): boolean => !!item.path || !!item.flyout;
const getReferencedAllCategoriesItem = (navigation: NavigationData, reference: string): NavigationItemWithFlyoutData | undefined => navigation.allCategories.find(cat => cat.id === reference);
const getReferencedAllCategoriesItemFlyout = (navigation: NavigationData, reference: string): NavigationFlyoutData | null => {
  const flyout = getReferencedAllCategoriesItem(navigation, reference)?.flyout;
  return flyout ?? null;
};
const getItemKey = (item: NavigationItemWithFlyoutData | NavigationMainItem): string => item.id ?? item.path ?? JSON.stringify(item);
const sidebarLabelClasses = (highlighted: boolean): string => classNames(styles['all-categories-sidebar__label'], {
  [styles['all-categories-sidebar__label--highlighted']]: highlighted
});
const AllCategoryItem = t0 => {
  const $ = _c(25);
  const {
    item
  } = t0;
  const [isOpened, setIsOpened] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setIsOpened(true);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleHover = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setIsOpened(false);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleHoverOut = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = t4 => {
      const {
        currentTarget,
        target
      } = t4;
      if (!currentTarget.contains(target)) {
        handleHoverOut();
      }
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const handleOnBlur = t3;
  let t4;
  if ($[3] !== item.flyout) {
    t4 = item.flyout && !isEmptyFlyout(item.flyout);
    $[3] = item.flyout;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = classNames(styles["all-categories-sidebar__item"], {
      [styles["all-categories-sidebar__item--with-flyout"]]: t4
    });
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = item.path ?? PATH_PLACEHOLDER;
  let t7;
  if ($[7] !== item.label) {
    t7 = event => {
      tracker.navigationClick({
        click_name: item.label ?? "unknown",
        click_position: "burger_menu",
        click_url: event.currentTarget.href
      });
    };
    $[7] = item.label;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== item.highlighted) {
    t8 = sidebarLabelClasses(item.highlighted);
    $[9] = item.highlighted;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== item.label || $[12] !== t8) {
    t9 = <span className={t8}>{item.label}</span>;
    $[11] = item.label;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t6 || $[15] !== t7 || $[16] !== t9) {
    t10 = <Link href={t6} className={styles["all-categories-sidebar__link"]} prefetch={false} onClick={t7}>{t9}</Link>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== isOpened || $[19] !== item.flyout) {
    t11 = item.flyout && isOpened && !isEmptyFlyout(item.flyout) && <div className={styles["desktop-navigation__all-categories-flyout"]}><NavigationFlyout flyout={item.flyout} isBurgerMenuFlyout={true} /></div>;
    $[18] = isOpened;
    $[19] = item.flyout;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t5) {
    t12 = <li className={t5} onMouseOver={handleHover} onFocus={handleHover} onMouseLeave={handleHoverOut} onBlur={handleOnBlur}>{t10}{t11}</li>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t5;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
};
export const DesktopNavigation = t0 => {
  const $ = _c(67);
  const {
    id,
    navigation,
    reducedView,
    showAllCategories
  } = t0;
  const {
    formatMessage
  } = useNamespacedIntl("desktop-navigation");
  const [toggledFlyOut, setToggledFlyOut] = useState(null);
  const [hoverActive, setHoverActive] = useState(false);
  const [isAllCategoriesEnabled, setIsAllCategoriesEnabled] = useState(false);
  const [hoverTimeout, setHoverTimeout] = useState();
  const [blurTimeout, setBlurTimeout] = useState();
  const scrollAreaRef = useRef(null);
  const [isFadedStart, setIsFadedStart] = useState(false);
  const [isFadedEnd, setIsFadedEnd] = useState(false);
  const [fadeLeftPx, setFadeLeftPx] = useState(0);
  const tasksPending = useSelector(selectTasksPending);
  const isHoverEnabled = hoverActive && !tasksPending;
  const activeToggledFlyOut = tasksPending ? null : toggledFlyOut;
  const activeIsAllCategoriesEnabled = !tasksPending && isAllCategoriesEnabled;
  const allCategories = navigation?.allCategories;
  let t1;
  if ($[0] !== allCategories) {
    t1 = allCategories ?? [];
    $[0] = allCategories;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const allItems = t1;
  let t2;
  if ($[2] !== allItems) {
    t2 = filterDesktop(allItems);
    $[2] = allItems;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const allItemsDesktop = t2;
  let t3;
  if ($[4] !== navigation) {
    const allTopItems = navigation?.main?.map(cat => {
      const reference = cat.reference;
      return {
        ...cat,
        flyout: reference ? getReferencedAllCategoriesItemFlyout(navigation, reference) : cat.flyout
      };
    }) ?? [];
    t3 = filterDesktop(allTopItems);
    $[4] = navigation;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const topItemsDesktop = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const el = scrollAreaRef.current;
      if (!el) {
        return;
      }
      const maxScrollLeft = el.scrollWidth - el.clientWidth;
      setIsFadedStart(el.scrollLeft > 1);
      setIsFadedEnd(el.scrollLeft < maxScrollLeft - 1);
      setFadeLeftPx(el.offsetLeft);
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const updateScrollFade = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = direction => {
      const el_0 = scrollAreaRef.current;
      if (!el_0) {
        return;
      }
      el_0.scrollBy({
        left: direction * el_0.clientWidth * 0.8,
        behavior: "smooth"
      });
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const scrollByViewport = t5;
  let t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const el_1 = scrollAreaRef.current;
      if (!el_1) {
        return;
      }
      updateScrollFade();
      el_1.addEventListener("scroll", updateScrollFade, {
        passive: true
      });
      const resizeObserver = new ResizeObserver(updateScrollFade);
      resizeObserver.observe(el_1);
      return () => {
        el_1.removeEventListener("scroll", updateScrollFade);
        resizeObserver.disconnect();
      };
    };
    t7 = [updateScrollFade];
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  let t8;
  if ($[10] !== hoverTimeout) {
    t8 = () => {
      setHoverActive(false);
      setIsAllCategoriesEnabled(false);
      setToggledFlyOut(null);
      clearTimeout(hoverTimeout);
      setBlurTimeout(undefined);
    };
    $[10] = hoverTimeout;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const clearHover = t8;
  let t9;
  if ($[12] !== clearHover) {
    t9 = () => {
      const timeout = setTimeout(() => {
        clearHover();
        clearTimeout(timeout);
      }, 500);
    };
    $[12] = clearHover;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const clearHoverWithDelay = t9;
  if (tasksPending && (hoverActive || toggledFlyOut !== null || isAllCategoriesEnabled)) {
    clearHover();
  }
  const t10 = activeIsAllCategoriesEnabled && isHoverEnabled;
  let t11;
  if ($[14] !== t10) {
    t11 = classNames(styles["all-categories-sidebar-wrapper"], {
      [styles["all-categories-sidebar-wrapper--with-flyout"]]: t10
    });
    $[14] = t10;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const allCategoriesClasses = t11;
  const topItemLabelClasses = _temp;
  let t12;
  if ($[16] !== blurTimeout) {
    t12 = () => {
      clearTimeout(blurTimeout);
      const timer = setTimeout(() => setHoverActive(true), 500);
      setHoverTimeout(timer);
    };
    $[16] = blurTimeout;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const handleNavigationHover = t12;
  let t13;
  if ($[18] !== clearHover || $[19] !== clearHoverWithDelay || $[20] !== isHoverEnabled) {
    t13 = () => {
      if (isHoverEnabled) {
        clearHoverWithDelay();
      } else {
        clearHover();
      }
    };
    $[18] = clearHover;
    $[19] = clearHoverWithDelay;
    $[20] = isHoverEnabled;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const handleHoverOut = t13;
  let t14;
  if ($[22] !== handleHoverOut) {
    t14 = t15 => {
      const {
        currentTarget,
        target
      } = t15;
      if (!currentTarget.contains(target)) {
        handleHoverOut();
      }
    };
    $[22] = handleHoverOut;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const handleOnBlur = t14;
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      setIsAllCategoriesEnabled(true);
      setToggledFlyOut(null);
    };
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const handleAllCategoriesHover = t15;
  let t16;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = selectedItem => {
      setToggledFlyOut(selectedItem);
      setIsAllCategoriesEnabled(false);
    };
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const handleTopItemsHover = t16;
  let allCategoriesListElm;
  if (reducedView) {
    const t17 = `${id}-placeholder`;
    let t18;
    if ($[26] !== t17) {
      t18 = <div className={styles["all-categories-sidebar__nav-placeholder"]}><PlaceholderComponent id={t17} ratio={1} /></div>;
      $[26] = t17;
      $[27] = t18;
    } else {
      t18 = $[27];
    }
    allCategoriesListElm = t18;
  } else {
    let t17;
    if ($[28] !== allItemsDesktop) {
      t17 = allItemsDesktop && allItemsDesktop.filter(hasOwnPathOrFlyout).map(_temp2);
      $[28] = allItemsDesktop;
      $[29] = t17;
    } else {
      t17 = $[29];
    }
    let t18;
    if ($[30] !== t17) {
      t18 = <ul className={styles["all-categories-sidebar__nav"]}>{t17}</ul>;
      $[30] = t17;
      $[31] = t18;
    } else {
      t18 = $[31];
    }
    allCategoriesListElm = t18;
  }
  let t17;
  if ($[32] !== isFadedEnd || $[33] !== isFadedStart) {
    t17 = classNames(styles["desktop-navigation"], {
      [styles["desktop-navigation--faded-start"]]: isFadedStart,
      [styles["desktop-navigation--faded-end"]]: isFadedEnd
    });
    $[32] = isFadedEnd;
    $[33] = isFadedStart;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  const t18 = `${fadeLeftPx}px`;
  let t19;
  if ($[35] !== t18) {
    t19 = {
      "--nav-fade-left": t18
    };
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  const t20 = t19 as React.CSSProperties;
  let t21;
  if ($[37] !== allCategoriesClasses || $[38] !== allCategoriesListElm || $[39] !== formatMessage || $[40] !== showAllCategories) {
    t21 = showAllCategories && <li key="all-categories" className={classNames(styles["desktop-navigation__item"], styles["desktop-navigation__item--all-categories"])} onMouseOver={handleAllCategoriesHover} onFocus={handleAllCategoriesHover}><button className={styles["all-categories-button"]}><BurgerIconComponent /><span className={classNames(styles["all-categories-button__label"])}>{formatMessage("allCategories")}</span></button><div className={allCategoriesClasses}><div className={styles["all-categories-sidebar"]}>{allCategoriesListElm}</div></div></li>;
    $[37] = allCategoriesClasses;
    $[38] = allCategoriesListElm;
    $[39] = formatMessage;
    $[40] = showAllCategories;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== activeToggledFlyOut || $[43] !== isHoverEnabled || $[44] !== showAllCategories || $[45] !== topItemsDesktop) {
    t22 = topItemsDesktop && topItemsDesktop.filter(hasOwnPathOrFlyout).map((item_0, itemIdx) => <li key={getItemKey(item_0)} className={styles["desktop-navigation__item"]}><Link prefetch={false} href={item_0.path ?? PATH_PLACEHOLDER} className={styles["desktop-navigation__link"]} onMouseEnter={() => handleTopItemsHover(itemIdx)} onFocus={() => handleTopItemsHover(itemIdx)} onClick={event => {
        tracker.navigationClick({
          click_name: item_0.label ?? "unknown",
          click_position: "header_main_navi",
          click_url: event.currentTarget.href
        });
      }}><span className={topItemLabelClasses(item_0.highlighted)}>{item_0.label}</span></Link>{isHoverEnabled && activeToggledFlyOut === itemIdx && item_0.flyout && !isEmptyFlyout(item_0.flyout) && <div className={classNames(styles["desktop-navigation__item-flyout-wrapper"], {
        [styles["desktop-navigation__item-flyout-wrapper--full-width"]]: !showAllCategories
      })}><div className={styles["desktop-navigation__item-flyout"]}><NavigationFlyout flyout={item_0.flyout} fullScreenWidth={!showAllCategories} /></div></div>}</li>);
    $[42] = activeToggledFlyOut;
    $[43] = isHoverEnabled;
    $[44] = showAllCategories;
    $[45] = topItemsDesktop;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== t22) {
    t23 = <li className={styles["desktop-navigation__scroll-item"]}><ul className={styles["desktop-navigation__scroll-area"]} ref={scrollAreaRef}>{t22}</ul></li>;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== t21 || $[50] !== t23) {
    t24 = <ul className={styles["desktop-navigation__items-list"]}>{t21}{t23}</ul>;
    $[49] = t21;
    $[50] = t23;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== formatMessage || $[53] !== isFadedStart) {
    t25 = isFadedStart && <button type="button" className={classNames(styles["desktop-navigation__scroll-button"], styles["desktop-navigation__scroll-button--start"])} aria-label={formatMessage("scrollBack")} onClick={() => scrollByViewport(-1)}><ImageComponent className={classNames(styles["desktop-navigation__scroll-icon"], styles["desktop-navigation__scroll-icon--back"])} src="/assets/icons/arrow_forward.svg" alt="" width={20} height={20} /></button>;
    $[52] = formatMessage;
    $[53] = isFadedStart;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== formatMessage || $[56] !== isFadedEnd) {
    t26 = isFadedEnd && <button type="button" className={classNames(styles["desktop-navigation__scroll-button"], styles["desktop-navigation__scroll-button--end"])} aria-label={formatMessage("scrollForward")} onClick={() => scrollByViewport(1)}><ImageComponent className={styles["desktop-navigation__scroll-icon"]} src="/assets/icons/arrow_forward.svg" alt="" width={20} height={20} /></button>;
    $[55] = formatMessage;
    $[56] = isFadedEnd;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== handleHoverOut || $[59] !== handleNavigationHover || $[60] !== handleOnBlur || $[61] !== t17 || $[62] !== t20 || $[63] !== t24 || $[64] !== t25 || $[65] !== t26) {
    t27 = <div className={t17} style={t20} onMouseOver={handleNavigationHover} onFocus={handleNavigationHover} onMouseLeave={handleHoverOut} onBlur={handleOnBlur}>{t24}{t25}{t26}</div>;
    $[58] = handleHoverOut;
    $[59] = handleNavigationHover;
    $[60] = handleOnBlur;
    $[61] = t17;
    $[62] = t20;
    $[63] = t24;
    $[64] = t25;
    $[65] = t26;
    $[66] = t27;
  } else {
    t27 = $[66];
  }
  return t27;
};
function _temp(highlighted) {
  return classNames(styles["desktop-navigation__item-label"], {
    [styles["desktop-navigation__item-label--highlighted"]]: highlighted
  });
}
function _temp2(item) {
  return <AllCategoryItem key={getItemKey(item)} item={item} />;
}